"use client";

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPost, apiPatch, apiDelete } from "@/lib/api-client";
import type { NotificationChannel, NotificationRecord } from "@/lib/api-client";
import { useAppStore } from "@/lib/store";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Mail,
  MessageCircle,
  Trash2,
  Plus,
  Bell,
  Power,
  Send,
  Pencil,
  FlaskConical,
} from "lucide-react";
import { formatDate } from "@/components/booking/format";
import { toast } from "sonner";
import { useState } from "react";
import { useI18n } from "@/lib/i18n/provider";

// Event types + presentation metadata.
const EVENT_TYPES = [
  "USER_SIGNUP",
  "USER_VALIDATED",
  "GUEST_INVITED",
  "RESERVATION_REQUEST",
  "RESERVATION_APPROVED",
  "RESERVATION_REJECTED",
  "BOOKING_ADDED",
  "EXPENSE_ASSIGNED",
  "EXPENSE_PAID",
  "TEST",
] as const;

const TYPE_BADGE: Record<string, string> = {
  USER_SIGNUP: "border-sky-200 bg-sky-50 text-sky-700",
  USER_VALIDATED: "border-emerald-200 bg-emerald-50 text-emerald-700",
  GUEST_INVITED: "border-sky-200 bg-sky-50 text-sky-700",
  RESERVATION_REQUEST: "border-amber-200 bg-amber-50 text-amber-800",
  RESERVATION_APPROVED: "border-emerald-200 bg-emerald-50 text-emerald-700",
  RESERVATION_REJECTED: "border-rose-200 bg-rose-50 text-rose-700",
  BOOKING_ADDED: "border-violet-200 bg-violet-50 text-violet-700",
  EXPENSE_ASSIGNED: "border-orange-200 bg-orange-50 text-orange-700",
  EXPENSE_PAID: "border-emerald-200 bg-emerald-50 text-emerald-700",
  TEST: "border-zinc-200 bg-zinc-50 text-zinc-600",
};

function parseTypes(csv: string): string[] {
  if (!csv || csv === "ALL") return [...EVENT_TYPES];
  return csv.split(",").map((t) => t.trim()).filter((t) => (EVENT_TYPES as readonly string[]).includes(t));
}

export function NotificationsView() {
  const user = useAppStore((s) => s.user);
  const qc = useQueryClient();
  const { t, locale } = useI18n();
  const [form, setForm] = useState({
    kind: "EMAIL" as "EMAIL" | "WHATSAPP",
    target: "",
    label: "",
    types: [] as string[],
  });
  const [typeFilter, setTypeFilter] = useState("ALL");
  const [editTypesFor, setEditTypesFor] = useState<NotificationChannel | null>(null);
  const [editTypes, setEditTypes] = useState<string[]>([]);

  const { data: channelsData } = useQuery({
    queryKey: ["notif-channels"],
    queryFn: () => apiGet<{ channels: NotificationChannel[] }>(
      "/api/admin/notifications/channels"
    ),
    enabled: !!user && user.role === "ADMIN",
  });
  const { data: historyData } = useQuery({
    queryKey: ["notif-history"],
    queryFn: () => apiGet<{ notifications: NotificationRecord[] }>(
      "/api/admin/notifications/history"
    ),
    enabled: !!user && (user.role === "ADMIN" || user.role === "MODERATOR"),
    refetchInterval: 30000,
  });

  const channels = channelsData?.channels ?? [];
  const history = historyData?.notifications ?? [];
  const filteredHistory =
    typeFilter === "ALL" ? history : history.filter((n) => n.type === typeFilter);
  const presentTypes = Array.from(new Set(history.map((n) => n.type)));

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["notif-channels"] });
    qc.invalidateQueries({ queryKey: ["notif-history"] });
  };

  const add = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.target) return;
    try {
      await apiPost("/api/admin/notifications/channels", {
        kind: form.kind,
        target: form.target,
        label: form.label || null,
        active: true,
        types: form.types.length ? form.types : undefined,
      });
      toast.success(t("toast.channelAdded"));
      setForm({ kind: "EMAIL", target: "", label: "", types: [] });
      refresh();
    } catch (err: any) {
      toast.error(err.message || "Could not add channel");
    }
  };

  const toggle = async (ch: NotificationChannel) => {
    try {
      await apiPatch(`/api/admin/notifications/channels/${ch.id}`, { active: !ch.active });
      toast.success(t("toast.channelUpdated"));
      refresh();
    } catch (err: any) {
      toast.error(err.message || "Could not update");
    }
  };

  const remove = async (id: string) => {
    try {
      await apiDelete(`/api/admin/notifications/channels/${id}`);
      toast.success(t("toast.channelRemoved"));
      refresh();
    } catch (err: any) {
      toast.error(err.message || "Could not remove");
    }
  };

  const sendTest = async (ch: NotificationChannel) => {
    try {
      const res = await apiPost<{ delivered: boolean }>(
        `/api/admin/notifications/test`,
        { channelId: ch.id }
      );
      refresh();
      if (res.delivered) toast.success(t("notif.testOk"));
      else toast.warning(t("notif.testNoProvider"));
    } catch (err: any) {
      toast.error(err.message || "Test failed");
    }
  };

  const saveTypes = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editTypesFor) return;
    try {
      await apiPatch(`/api/admin/notifications/channels/${editTypesFor.id}`, {
        types: editTypes,
      });
      toast.success(t("toast.channelUpdated"));
      setEditTypesFor(null);
      refresh();
    } catch (err: any) {
      toast.error(err.message || "Could not update");
    }
  };

  const typeBadge = (type: string) => (
    <Badge variant="outline" className={TYPE_BADGE[type] ?? "border-border text-muted-foreground"}>
      {t(`notif.type.${type}`)}
    </Badge>
  );

  const typesEditor = (
    <div className="grid gap-1.5">
      <Label>{t("notif.typesTitle")}</Label>
      <p className="mb-1 text-xs text-muted-foreground">{t("notif.typesHint")}</p>
      <div className="grid gap-1.5 sm:grid-cols-2">
        {EVENT_TYPES.map((et) => {
          const checked =
            editTypesFor ? editTypes.includes(et) : form.types.includes(et);
          const toggleType = (et: string) => {
            if (editTypesFor)
              setEditTypes((list) =>
                list.includes(et) ? list.filter((x) => x !== et) : [...list, et]
              );
            else
              setForm((f) => ({
                ...f,
                types: f.types.includes(et)
                  ? f.types.filter((x) => x !== et)
                  : [...f.types, et],
              }));
          };
          return (
            <label
              key={et}
              className="flex cursor-pointer items-center gap-2 rounded-md border px-2.5 py-1.5 text-sm hover:bg-muted"
            >
              <Checkbox checked={checked} onCheckedChange={() => toggleType(et)} />
              {t(`notif.type.${et}`)}
            </label>
          );
        })}
      </div>
      {(editTypesFor ? editTypes.length : form.types.length) === 0 && (
        <p className="text-xs text-muted-foreground">{t("notif.allTypesFallback")}</p>
      )}
    </div>
  );

  return (
    <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6">
      <div className="mb-6">
        <h1 className="flex items-center gap-2 text-2xl font-bold tracking-tight">
          <Bell className="h-6 w-6 text-amber-600" /> {t("notif.title")}
        </h1>
        <p className="mt-1 text-muted-foreground">{t("notif.subtitle")}</p>
      </div>

      {user?.role === "ADMIN" && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-base">{t("notif.addEmail")} / {t("notif.addWhatsapp")}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={add} className="grid gap-3 sm:grid-cols-[140px_1fr_1fr_auto]">
              <div className="grid gap-1.5">
                <Label>{t("notif.kind")}</Label>
                <Select value={form.kind} onValueChange={(v) => setForm((f) => ({ ...f, kind: v as any }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="EMAIL">{t("notif.EMAIL")}</SelectItem>
                    <SelectItem value="WHATSAPP">{t("notif.WHATSAPP")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-1.5">
                <Label>{t("notif.target")}</Label>
                <Input
                  required
                  type={form.kind === "EMAIL" ? "email" : "tel"}
                  value={form.target}
                  onChange={(e) => setForm((f) => ({ ...f, target: e.target.value }))}
                  placeholder={form.kind === "EMAIL" ? "alerts@homebook.demo" : "+33123456789"}
                />
              </div>
              <div className="grid gap-1.5">
                <Label>{t("notif.label")}</Label>
                <Input
                  value={form.label}
                  onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
                />
              </div>
              <div className="flex items-end">
                <Button type="submit" className="gap-1.5">
                  <Plus className="h-4 w-4" /> {t("common.add")}
                </Button>
              </div>
            </form>
            <div className="mt-4">{typesEditor}</div>
          </CardContent>
        </Card>
      )}

      {user?.role === "ADMIN" && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-base">
              {t("notif.channelsTitle")} ({channels.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {channels.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t("notif.noChannels")}</p>
            ) : (
              <div className="grid gap-2">
                {channels.map((ch) => {
                  const chTypes = parseTypes(ch.types ?? "ALL");
                  return (
                    <div
                      key={ch.id}
                      className="rounded-lg border bg-card px-3 py-2.5 text-sm"
                    >
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="flex min-w-0 items-center gap-2">
                          {ch.kind === "EMAIL" ? (
                            <Mail className="h-4 w-4 shrink-0 text-sky-600" />
                          ) : (
                            <MessageCircle className="h-4 w-4 shrink-0 text-emerald-600" />
                          )}
                          <div className="min-w-0">
                            <div className="truncate font-medium">{ch.target}</div>
                            {ch.label && (
                              <div className="text-xs text-muted-foreground">{ch.label}</div>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-wrap items-center gap-1.5">
                          <Badge variant="outline">{t(`notif.${ch.kind}`)}</Badge>
                          <Badge
                            variant="outline"
                            className={ch.active ? "border-emerald-200 text-emerald-700" : "border-zinc-300 text-zinc-500"}
                          >
                            {ch.active ? t("notif.active") : t("notif.inactive")}
                          </Badge>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 gap-1.5"
                            onClick={() => {
                              setEditTypesFor(ch);
                              setEditTypes(chTypes);
                            }}
                          >
                            <Pencil className="h-3.5 w-3.5" /> {t("notif.editEvents")}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 gap-1.5"
                            onClick={() => sendTest(ch)}
                          >
                            <FlaskConical className="h-3.5 w-3.5" /> {t("notif.sendTest")}
                          </Button>
                          <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => toggle(ch)}>
                            <Power className="h-4 w-4" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8 text-destructive hover:text-destructive"
                            onClick={() => remove(ch.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      <div className="mt-1.5 flex flex-wrap items-center gap-1">
                        <span className="text-xs text-muted-foreground">{t("notif.typesTitle")}:</span>
                        {chTypes.length === EVENT_TYPES.length ? (
                          <Badge variant="outline" className="h-5">{t("notif.allTypes")}</Badge>
                        ) : (
                          chTypes.map((tp) => (
                            <Badge key={tp} variant="outline" className={`h-5 ${TYPE_BADGE[tp] ?? ""}`}>
                              {t(`notif.type.${tp}`)}
                            </Badge>
                          ))
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <CardTitle className="text-base">{t("notif.history")} ({filteredHistory.length})</CardTitle>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[220px]">
                <SelectValue placeholder={t("notif.filterType")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">{t("notif.all")}</SelectItem>
                {presentTypes.map((tp) => (
                  <SelectItem key={tp} value={tp}>
                    {t(`notif.type.${tp}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {filteredHistory.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t("notif.noHistory")}</p>
          ) : (
            <div className="max-h-96 space-y-2 overflow-y-auto">
              {filteredHistory.map((n) => (
                <div key={n.id} className="rounded-lg border bg-card px-3 py-2 text-sm">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex flex-wrap items-center gap-2">
                      {typeBadge(n.type)}
                      <span className="font-medium">{n.title}</span>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {formatDate(n.createdAt, locale)}
                    </span>
                  </div>
                  <p className="mt-1 whitespace-pre-line text-xs text-muted-foreground">{n.body}</p>
                  {n.dispatch.length > 0 && (
                    <div className="mt-1 flex flex-wrap gap-1">
                      {n.dispatch.map((d, i) => (
                        <Badge
                          key={i}
                          variant="outline"
                          className={
                            d.ok
                              ? "border-emerald-200 text-emerald-700"
                              : "border-rose-200 text-rose-700"
                          }
                        >
                          {d.channel} → {d.target} {d.ok ? "✓" : "✗"}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit subscribed event types */}
      <Dialog open={!!editTypesFor} onOpenChange={(o) => !o && setEditTypesFor(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{t("notif.editEvents")}</DialogTitle>
            <DialogDescription>{editTypesFor?.target}</DialogDescription>
          </DialogHeader>
          <form onSubmit={saveTypes} className="grid gap-4">
            {typesEditor}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditTypesFor(null)}>
                {t("common.cancel")}
              </Button>
              <Button type="submit">{t("common.save")}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
